import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { ImageRef } from '@gooduncles/gu-app-schema';

import ImageNodeView from './ImageNodeView';

export interface TiptapImageOptions {
  inline: boolean;
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    customImage: {
      setImage: (options: { imageFileId: string; src: string }) => ReturnType;
    };
  }
}

/**
 * imageFileId 기반 커스텀 Image extension.
 * - imageFileId: images Map의 키 (영구 참조용)
 * - src: 실제 표시할 이미지 URL (렌더링용)
 *
 * Firestore에 저장 시 content JSON에는 imageFileId + src가 함께 저장되고,
 * AVIF 변환 등으로 URL이 변경되면 images Map의 URL이 정본이 된다.
 */
const TiptapImageExtension = Node.create<TiptapImageOptions>({
  name: 'image',
  group: 'block',
  draggable: true,
  atom: true,

  addOptions() {
    return {
      inline: false,
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      imageFileId: {
        default: null,
      },
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      width: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img[data-image-file-id]',
        getAttrs: (dom) => {
          const element = dom as HTMLElement;
          return {
            imageFileId: element.getAttribute('data-image-file-id'),
            src: element.getAttribute('src'),
            alt: element.getAttribute('alt'),
            width: element.getAttribute('width'),
          };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'img',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-image-file-id': node.attrs.imageFileId,
        src: node.attrs.src ?? '',
        alt: node.attrs.alt ?? '',
        style: node.attrs.width ? `width: ${node.attrs.width}px` : 'max-width: 100%',
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView);
  },

  addCommands() {
    return {
      setImage:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },
});

export default TiptapImageExtension;

/**
 * TipTap content JSON 내의 image 노드에 images Map의 최신 URL을 주입합니다.
 * 기존 공지사항 수정 시 content 로드할 때 사용합니다.
 */
export const resolveContentImageUrls = (
  content: Record<string, any> | null,
  images: Record<string, ImageRef>,
): Record<string, any> | null => {
  if (!content) return null;

  const resolveNode = (node: Record<string, any>): Record<string, any> => {
    if (node.type === 'image' && node.attrs?.imageFileId) {
      const imageRef = images[node.attrs.imageFileId];
      return {
        ...node,
        attrs: {
          ...node.attrs,
          src: imageRef?.originalUrl ?? imageRef?.thumbnailUrl ?? node.attrs.src ?? '',
        },
      };
    }

    if (node.content) {
      return {
        ...node,
        content: node.content.map(resolveNode),
      };
    }

    return node;
  };

  return resolveNode(content);
};
